import { unifiedIsoCountries } from "@core/dictionary/global";
import { useState } from "react";
import DataTable from "react-data-table-component";
import { ChevronDown, ChevronUp } from "react-feather";
import ReactTooltip from "react-tooltip";
import ExpandedViewModal from "./expandedViewModal";

const namingHeaders = {
  general_duty: "General/MFN Duty rate",
  other_taxes: "Other Duties/Taxes",
  preferential_duty_rate: "Preferential Duty Rate",
  vat_rates: "VAT/GST Taxes",
};

const customStyles = {
  table: {
    style: {
      height: "auto",
      overflow: "hidden",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f9fafb",
      height: "32px !important",
    },
  },
  headCells: {
    style: {
      color: "#101828",
      fontSize: "12px !important",
      fontWeight: "500",
    },
  },
  rows: {
    style: {
      minHeight: "48px",
    },
  },
  cells: {
    style: {
      color: "#101828",
      fontSize: "12px",
    },
  },
};

const TooltipWrapper = ({ text, maxLength }) => {
  const content = text && text !== "" ? text : "";
  const id = `tooltip-${Math.random().toString(36).substr(2, 9)}`;
  const showTooltip = text && text.length > maxLength;

  if (!showTooltip) {
    return <span className="tw-inline-block tw-truncate">{content}</span>;
  }

  return (
    <span className="tw-inline-block tw-truncate" data-tip={text} data-for={id}>
      {content.slice(0, maxLength) + "..."}
      <ReactTooltip id={id} className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="top" />
    </span>
  );
};

const TariffsData = ({ data, countryOfImport }) => {
  const [openAccordions, setOpenAccordions] = useState({ 0: true });
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const toggleAccordion = (index) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleOpenModal = (data, type) => {
    setModalData({ ...data, modalType: type });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalData(null);
  };

  const countryOptions = Object.entries(unifiedIsoCountries).map(([value, label]) => ({ value, label }));

  const filteredCont = countryOptions.filter((item) => {
    if (item.value === countryOfImport.labelvalue || item.value === "UK" || item.value === "EU" || item.value === "ALL") {
      return false;
    }
    return true;
  });

  filteredCont.sort((a, b) => (a.label > b.label ? 1 : -1));
  filteredCont.unshift({ value: "ALL", label: "All Countries" });

  const uom = (val) => {
    if (typeof val === "string" && val.length > 0 && val !== "-") {
      return val;
    } else if (Array.isArray(val) && val.length > 0) {
      return val.join(", ");
    } else {
      return "-";
    }
  };

  const mapCountryNames = (country) => {
    if (!country || country === "-" || (Array.isArray(country) && country.length === 0)) {
      return "-";
    }
    if (typeof country === "string") {
      if (country.toLowerCase().includes("all")) {
        return "All Countries";
      }
      return unifiedIsoCountries[country] ?? country ?? "-";
    }
    if (Array.isArray(country)) {
      const allCountries = country.some((c) => typeof c === "string" && c.toLowerCase().includes("all"));
      if (allCountries) {
        return "All Countries";
      }

      const mapped = country.map((item) => unifiedIsoCountries[item] ?? item ?? "--").join(", ");

      return mapped.length > 0 ? mapped : "-";
    }
    return "-";
  };

  const generateDutiesColumns = (dutyType, columnConfig) => {
    const config = columnConfig[dutyType];

    if (!config) {
      return [];
    }

    const columns = [];

    Object.keys(config).forEach((key) => {
      const columnName = config[key];

      if (key === "duty_name") {
        columns.push({
          name: columnName,
          selector: (row) => <TooltipWrapper text={row?.duty_name} maxLength={25} />,
          width: "200px",
        });
      } else if (key === "destination") {
        columns.push({
          name: columnName,
          selector: (row) => <TooltipWrapper text={mapCountryNames(row?.destination)} maxLength={30} />,
          width: "250px",
        });
      } else if (key === "duty_rate") {
        columns.push({
          name: columnName,
          selector: (row) => <TooltipWrapper text={uom(row?.duty_rate)} maxLength={15} />,
          width: "150px",
        });
      } else if (key === "duty_type") {
        columns.push({
          name: columnName,
          selector: (row) => <TooltipWrapper text={row?.duty_type} maxLength={20} />,
          width: "150px",
        });
      } else if (key === "unit_of_measurement") {
        columns.push({
          name: columnName,
          selector: (row) => <TooltipWrapper text={uom(row?.unit_of_measurement)} maxLength={15} />,
          width: "150px",
        });
      } else if (key === "effective_date_from") {
        columns.push({
          name: columnName,
          selector: (row) => (row?.effective_date_from && row?.effective_date_from !== "" ? row?.effective_date_from : "-"),
          width: "150px",
        });
      } else if (key === "expiry_date") {
        columns.push({
          name: columnName,
          selector: (row) => (row?.expiry_date ? row?.expiry_date : "-"),
          width: "150px",
        });
      } else if (key === "excluding_country") {
        columns.push({
          name: columnName,
          selector: (row) => <TooltipWrapper text={mapCountryNames(row?.excluding_country)} maxLength={30} />,
          width: "250px",
        });
      } else if (key === "footnotes") {
        columns.push({
          name: columnName,
          selector: (row) => {
            return (
              <div className="tw-flex tw-items-center tw-gap-2">
                <TooltipWrapper text={row?.footnotes} maxLength={30} />
                {row.is_footnote_data && row.footnote_data && (
                  <div className="tw-cursor-pointer tw-gap-2 tw-flex tw-items-center tw-justify-center" onClick={() => handleOpenModal(row.footnote_data, "footnotes")}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#7143f1">
                      <path d="m256-240-56-56 384-384H240v-80h480v480h-80v-344L256-240Z" />
                    </svg>
                  </div>
                )}
              </div>
            );
          },
          width: "250px",
        });
      } else if (key === "conditions") {
        columns.push({
          name: columnName,
          selector: (row) => {
            return (
              <div className="tw-flex tw-items-center tw-gap-2">
                <TooltipWrapper text={row?.conditions} maxLength={30} />
                {row.is_condition_data && row.condition_data && (
                  <div className="tw-cursor-pointer tw-gap-2 tw-flex tw-items-center tw-justify-center" onClick={() => handleOpenModal(row.condition_data, "conditions")}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#7143f1">
                      <path d="m256-240-56-56 384-384H240v-80h480v480h-80v-344L256-240Z" />
                    </svg>
                  </div>
                )}
              </div>
            );
          },
          width: "250px",
        });
      } else if (key === "preferential_program") {
        columns.push({
          name: columnName,
          selector: (row) => <TooltipWrapper text={row?.preferential_program} maxLength={38} />,
          width: "300px",
        });
      } else if (key === "additional_codes") {
        columns.push({
          name: columnName,
          selector: (row, index) => {
            return (
              <>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {row?.additional_codes && (
                    <div data-tip={row.additional_codes} data-for={`${row.additional_codes}${index}`}>
                      {row.additional_codes}
                    </div>
                  )}
                  {row.is_additional_code_data && row.additional_code_data && (
                    <div className="tw-cursor-pointer tw-gap-2 tw-flex tw-items-center tw-justify-center" onClick={() => handleOpenModal(row.additional_code_data, "additional_codes")}>
                      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#7143f1">
                        <path d="m256-240-56-56 384-384H240v-80h480v480h-80v-344L256-240Z" />
                      </svg>
                    </div>
                  )}
                </div>
                {row?.additional_codes?.length > 30 && <ReactTooltip className="trademo-tooltip" id={`${row.additional_codes}${index}`} place="top" />}
              </>
            );
          },
          width: "250px",
        });
      }
    });

    return columns;
  };

  const getDutiesColumns = (dutyType) => {
    if (data && data.column_config) {
      return generateDutiesColumns(dutyType, data.column_config);
    }
    return [];
  };

  const finalData = Object.keys(data).filter((key) => key !== "column_config");

  return (
    <>
      <div className="tw-overflow-auto tw-divide-y">
        {finalData.map((name, index) => (
          <div key={`${name}${index}`}>
            <div className="tw-w-full tw-py-2.5 tw-text-left tw-flex tw-justify-between tw-items-center tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500 tw-focus:ring-inset" onClick={() => toggleAccordion(index)} type="button">
              <span className="tw-font-medium tw-text-gray-900 tw-text-sm">{namingHeaders[name]}</span>
              {openAccordions[index] ? <ChevronUp className="tw-h-5 tw-w-5 tw-text-gray-500 tw-transition-transform tw-duration-200" /> : <ChevronDown className="tw-h-5 tw-w-5 tw-text-gray-500 tw-transition-transform tw-duration-200" />}
            </div>

            {openAccordions[index] && (
              <div className="tw-transition-all tw-duration-300 tw-ease-in-out">
                <div className="tw-bg-white tw-p-2">{data[name] && data[name].length > 0 ? <DataTable columns={getDutiesColumns(name)} data={data[name]} customStyles={customStyles} pagination paginationTotalRows={data[name].length} paginationPerPage={10} persistTableHead highlightOnHover={false} /> : <div className="tw-text-center tw-text-gray-500 tw-py-8">No data available</div>}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <ExpandedViewModal show={showModal} onHide={handleCloseModal} modalData={modalData} />
    </>
  );
};

export default TariffsData;
